import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Grid, TextField, Typography, useTheme, useMediaQuery, Checkbox } from '@mui/material';
import Row from 'src/frontend-common/components/Row';
import SettingsIcon from '@mui/icons-material/Settings';
import { CustomInputAdornment } from 'src/style/common';
import ButtonComponent from 'src/components/ButtonComponent';
import CloseIcon from '@mui/icons-material/Close';
import { useGetScoreConfigurationQuery, useUpdateScoreConfigurationMutation } from 'src/redux/services/score';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';

import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

export interface Field<T> {
  label: string;
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
}

export interface Props {
  appKey: string;
  scoreRate: string;
  setScoreRate: React.Dispatch<React.SetStateAction<string>>;

  loginRate: string;
  setLoginRate: React.Dispatch<React.SetStateAction<string>>;

  botDetectionRate: string;
  setBotDetectionRate: React.Dispatch<React.SetStateAction<string>>;
}

const CustomBillingConfiguration: React.FC<Props> = ({ appKey, scoreRate, setScoreRate, loginRate, setLoginRate, botDetectionRate, setBotDetectionRate }) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [phoneChecked, setPhoneChecked] = useState<boolean>(false);
  const [emailChecked, setEmailChecked] = useState<boolean>(false);
  const [ipChecked, setIpChecked] = useState<boolean>(false);
  const [deviceChecked, setDeviceChecked] = useState<boolean>(false);
  const [browserChecked, setBrowserChecked] = useState<boolean>(false);
  const [nameChecked, setNameChecked] = useState<boolean>(false);
  const [vatCodeChecked, setVatCodeChecked] = useState<boolean>(false);
  const [taxIdChecked, setTaxIdChecked] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: scoreConfig, isLoading: scoreConfigIsLoading, isError: scoreConfigIsError } = useGetScoreConfigurationQuery({ appKey: appKey });

  const [updateScoreConfig, { isSuccess: updateScoreConfigIsSuccess, isError: updateScoreConfigIsError, error: updateScoreConfigError }] =
    useUpdateScoreConfigurationMutation();

  const fields: Field<boolean>[] = [
    {
      label: 'Phone',
      value: phoneChecked,
      setValue: setPhoneChecked,
    },
    {
      label: 'Email',
      value: emailChecked,
      setValue: setEmailChecked,
    },
    {
      label: 'IP Address',
      value: ipChecked,
      setValue: setIpChecked,
    },
    {
      label: 'Device',
      value: deviceChecked,
      setValue: setDeviceChecked,
    },
    {
      label: 'Browser',
      value: browserChecked,
      setValue: setBrowserChecked,
    },
    {
      label: 'Name',
      value: nameChecked,
      setValue: setNameChecked,
    },
    {
      label: 'VAT Code',
      value: vatCodeChecked,
      setValue: setVatCodeChecked,
    },
    {
      label: 'TAX ID',
      value: taxIdChecked,
      setValue: setTaxIdChecked,
    },
  ];

  useEffect(() => {
    if (scoreConfig) {
      setPhoneChecked(scoreConfig.item.phone);
      setEmailChecked(scoreConfig.item.email);
      setIpChecked(scoreConfig.item.ip);
      setDeviceChecked(scoreConfig.item.device);
      setBrowserChecked(scoreConfig.item.browser);
      setNameChecked(scoreConfig.item.name);
      setVatCodeChecked(scoreConfig.item.vat_code);
      setTaxIdChecked(scoreConfig.item.tax_id);
    }
  }, [scoreConfig, appKey]);

  const handleUpdate = () => {
    updateScoreConfig({
      app_key: appKey,
      phone: phoneChecked,
      email: emailChecked,
      ip: ipChecked,
      device: deviceChecked,
      browser: browserChecked,
      name: nameChecked,
      vat_code: vatCodeChecked,
      tax_id: taxIdChecked,
    });
  };

  return (
    <>
      <Typography variant="h5" style={{ marginBottom: theme.spacing(3), marginTop: theme.spacing(4) }}>
        Solutions
      </Typography>
      <Grid container mb={3} spacing={3}>
        <Grid item xs={12} md={6}>
          <Row vAlign="center">
            <Typography variant="h6">Onboarding</Typography>
            <Button
              variant="text"
              style={{
                marginRight: theme.spacing(4),
                padding: theme.spacing(0.75),
                minWidth: 20,
              }}
              onClick={() => setModalIsOpen(true)}
            >
              <SettingsIcon style={{ fontSize: 18, color: '#151515' }} />
            </Button>
            <TextField
              variant="outlined"
              fullWidth={true}
              value={scoreRate}
              error={scoreRate.trim() === '' || Number(scoreRate) < 0}
              label="Price"
              type="number"
              onChange={(result) => setScoreRate(result.target.value)}
              InputProps={{
                startAdornment: <CustomInputAdornment position="start">{useGetCurrency()}</CustomInputAdornment>,
              }}
            />
          </Row>
        </Grid>
        <Grid item xs={12} md={6}>
          <Row vAlign="center">
            <Typography
              variant="h6"
              style={{
                marginRight: theme.spacing(4),
                padding: theme.spacing(0.75),
                minWidth: 20,
              }}
            >
              Login
            </Typography>
            <TextField
              variant="outlined"
              fullWidth={true}
              value={loginRate}
              error={loginRate.trim() === '' || Number(loginRate) < 0}
              label="Price"
              type="number"
              onChange={(result) => setLoginRate(result.target.value)}
              InputProps={{
                startAdornment: <CustomInputAdornment position="start">{useGetCurrency()}</CustomInputAdornment>,
              }}
            />
          </Row>
        </Grid>
        <Grid item xs={12} md={6}>
          <Row vAlign="center">
            <Typography
              variant="h6"
              style={{
                marginRight: theme.spacing(4),
                padding: theme.spacing(0.75),
                minWidth: 20,
              }}
            >
              Session
            </Typography>
            <TextField
              variant="outlined"
              fullWidth={true}
              value={botDetectionRate}
              error={botDetectionRate.trim() === '' || Number(botDetectionRate) < 0}
              label="Price"
              type="number"
              onChange={(result) => setBotDetectionRate(result.target.value)}
              InputProps={{
                startAdornment: <CustomInputAdornment position="start">{useGetCurrency()}</CustomInputAdornment>,
              }}
            />
          </Row>
        </Grid>
      </Grid>
      <Dialog fullWidth={true} maxWidth={isMobile ? 'xs' : 'md'} open={modalIsOpen}>
        <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, padding: 0 }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ padding: theme.spacing(3) }}>
            <Typography variant="h4" data-testid="request-a-demo-modal-title" sx={{ color: '#fff' }}>
              Onboarding configuration
            </Typography>
            <ButtonComponent
              data-testid="request-close-modal"
              buttontype="icon"
              icon={<CloseIcon />}
              onClick={() => setModalIsOpen(false)}
              sx={{ color: '#fff' }}
            />
          </Grid>
        </DialogTitle>
        <DialogContent style={{ overflow: 'hidden', padding: 0 }}>
          {scoreConfigIsLoading && <LoadingScreen />}
          {!scoreConfigIsLoading && scoreConfigIsError && (
            <FailureScreen testId="score-config-fail">An error occurred while fetching the score configuration, please retry</FailureScreen>
          )}
          {!scoreConfigIsLoading && !scoreConfigIsError && scoreConfig && (
            <>
              <Grid container spacing={2} sx={{ padding: theme.spacing(2) }}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    sx={{
                      marginLeft: theme.spacing(1),
                      marginTop: theme.spacing(2),
                      fontSize: 18,
                    }}
                  >
                    Onboarding
                  </Typography>
                </Grid>
                {fields.map((field) => (
                  <Grid item xs={6} md={3} key={field.label}>
                    <Row vAlign="center">
                      <>
                        <Checkbox checked={field.value} onChange={() => field.setValue((prev) => !prev)} />
                        {field.label}
                      </>
                    </Row>
                  </Grid>
                ))}
              </Grid>
              <Row
                hAlign="flex-end"
                style={{
                  borderTop: `1px solid ${theme.palette.grey[300]}`,
                  padding: theme.spacing(3, 4),
                  marginTop: theme.spacing(2),
                }}
              >
                <ButtonComponent buttontype="cancel" onClick={() => setModalIsOpen(false)} sx={{ marginRight: theme.spacing(3) }} />

                <ButtonComponent buttontype="mainaction" text="Update" onClick={handleUpdate} />
              </Row>
            </>
          )}
        </DialogContent>
      </Dialog>
      <SnackBarCompleteHandler
        isSuccess={updateScoreConfigIsSuccess}
        isError={updateScoreConfigIsError}
        error={updateScoreConfigError}
        successMessage="Score configuration successfully updated"
      />
    </>
  );
};

export default CustomBillingConfiguration;
